import React, { useMemo, useCallback } from 'react';
import { formatCustomDate } from '../../helpers/formatCustomDate';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import StatusBadge from '../../generals/StatusBadge';

const ManualJournalDetailsContent = React.memo(React.forwardRef((props, ref) => {
    const { details, detailsList, id } = props;
    
    // Memoize calculations to avoid recalculating on every render
    const { totalDebt, totalCredit } = useMemo(() => {
        if (!Array.isArray(detailsList) || detailsList.length === 0) {
            return { totalDebt: 0, totalCredit: 0 };
        }

        return detailsList.reduce((acc, item) => ({
            totalDebt: acc.totalDebt + (item.debit_amount || 0),
            totalCredit: acc.totalCredit + (item.credit_amount || 0)
        }), { totalDebt: 0, totalCredit: 0 });
    }, [detailsList]);

    // Memoize formatted date to avoid recalculation
    const formattedDate = useMemo(() => 
        formatCustomDate(details?.encoded_date), 
        [details?.encoded_date]
    );

    // Memoize table rows to prevent unnecessary re-renders
    const tableRows = useMemo(() => {
        if (!Array.isArray(detailsList)) return null;
        
        return detailsList.map((item, index) => (
            <tr key={item.account_id || index}>
                <td className="border border-gray-200 p-2 text-sm text-gray-600 text-right">
                    {item?.account_id} 
                </td>
                <td className="border border-gray-200 text-sm p-2 text-gray-600 text-left">
                    {item?.account_name} 
                </td>
                <td className="border border-gray-200 p-2 text-sm text-gray-600">
                    {item?.remark}
                </td>
                <td className="border border-gray-200 p-2 text-sm text-gray-600 text-right">
                    {formatCustomNumber(item?.debit_amount)}
                </td>
                <td className="border border-gray-200 p-2 text-sm text-gray-600 text-right">
                    {formatCustomNumber(item?.credit_amount)}
                </td>
            </tr>
        ));
    }, [detailsList]);

    return (
        <div ref={ref} className="bg-white shadow-lg rounded-lg p-8 mx-auto">
            <div className="bg-white shadow-md rounded-md mx-auto my-8 p-6">
                <header className="flex justify-between items-center border-b pb-4">
                    <h1 className="text-xl font-bold uppercase">Journal</h1>
                    <p className="text-sm text-gray-500">#{details?.reference_number}</p>
                    <div className="m-2 p-2">
                        <StatusBadge
                            statusId={details?.status_id}
                            statusName={details?.status_name}
                            size="md"
                        />
                    </div>
                </header>

                <div className="mt-2">
                    <div className="flex justify-between">
                        <p className="text-sm text-gray-600">
                            <span className="font-medium">Journal Number:</span> {details?.journal_number}
                        </p>
                        <p className="text-sm text-gray-600">
                            <span className="font-medium">Date:</span> {formattedDate}
                        </p>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Reference Number:</span> {details?.reference_number}
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Journal Type:</span> {details?.journal_type_name}
                    </p> 
                    <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Transaction Type:</span> {details?.transation_type_name}
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Notes:</span> {details?.notes}
                    </p>
                </div>

                <table className="w-full mt-6 border-collapse border border-gray-200">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                                No.
                            </th>
                            <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                                Account
                            </th>
                            <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                                Remark
                            </th>
                            <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                                Debits
                            </th>
                            <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                                Credits
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td className="border border-gray-200 font-bold text-gray-800">
                                Total
                            </td>
                            <td className="border border-gray-200 px-4 py-2">-</td>
                            <td className="border border-gray-200 px-4 py-2">-</td>
                            <td className="border border-gray-200 px-4 py-2 text-right font-bold text-red-600">
                                NGN {formatCustomNumber(totalDebt)}
                            </td>
                            <td className="border border-gray-200 text-right font-bold text-green-600">
                                NGN {formatCustomNumber(totalCredit)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {details?.attachement_url && (
                <div className="flex justify-center">
                    <a 
                        className="btn btn-primary" 
                        rel="noreferrer" 
                        href={details?.attachement_url} 
                        target="_blank"
                    >
                        Open Attachment
                    </a>
                </div>
            )}
        </div>
    );
}));

ManualJournalDetailsContent.displayName = 'ManualJournalDetailsContent';

export default ManualJournalDetailsContent;